// Shared with mju-ai-workbench/shared/schemas/relayChannel.ts — keep the two identical. AIOE owns it (D343).
/**
 * Relay channels: the WebSocket leg of the relay (D343, docs/plans/2026-10-relay-channels.md), for what a 25 s long
 * poll serves badly — a live terminal, a streaming transcript. One relay, not a second: the same broker, the same
 * tenant and device binding, the same scopes and the same signed control request as `POST …/devices/:id/rpc`.
 * Approvals, one-off calls and the Inbox stay on HTTP.
 *
 *   POST /relay/v1/devices/:deviceId/channels/ticket   (person, bearer)   -> ChannelTicket   for the client socket
 *   GET  /relay/v1/devices/:deviceId/channels          WebSocket, subprotocol `aioe.channels.v1`, `?ticket=`
 *   POST /relay/v1/workbench/channels/ticket            (device, bearer/DPoP) -> ChannelTicket for the workbench socket
 *   GET  /relay/v1/workbench/channels                   WebSocket, subprotocol `aioe.channels.v1`, `?ticket=`
 *
 * A ticket is single-use and lives 30 s, so no token ever sits in a URL or a log. A socket carries many channels;
 * each channel is one subscription (one terminal, one transcript) opened by the client, named by an id the relay
 * assigns, and closed by either end. Frames are JSON text; nothing binary.
 *
 * Client socket:   client -> relay: open, input, credit, close, ping     relay -> client: opened, data, closed, pong, error
 * Workbench socket: relay -> workbench: open, input, credit, close, ping  workbench -> relay: opened, data, closed, pong
 *
 * The relay checks every client `open` as it checks an RPC (the person may reach the device, the device was granted
 * the method's scope) and passes the workbench the platform's control JWS over the open's `rpc`, which the workbench
 * verifies before serving (`verifyControlRequest`), as on the SSE path. `input` is allowed only on a channel whose
 * open asked for input (`input: true`) and was granted it; the workbench decides which methods take input and
 * refuses the rest. The relay never reads `data` or `input` payloads, never logs them, and counts frames and bytes
 * only. A revoked token or a removed member ends the socket within a minute (the stream recheck).
 */
import { z } from "zod";
import { controlScopeSchema, jsonRpcRequestSchema } from "./relay.js";
export const RELAY_CHANNEL_SUBPROTOCOL = "aioe.channels.v1";
/** The most a frame may carry, as UTF-8 bytes of its JSON. A larger frame closes the socket (1009). */
export const RELAY_CHANNEL_MAX_FRAME = 64 * 1024;
/** Open channels on one socket, and on one device across every socket. */
export const RELAY_CHANNEL_MAX_PER_SOCKET = 8;
export const RELAY_CHANNEL_MAX_PER_DEVICE = 32;
/** Bytes of `data` the relay forwards before the receiver grants more `credit`. */
export const RELAY_CHANNEL_INITIAL_CREDIT = 256 * 1024;
/** Seconds without a frame (a ping counts) before the relay closes a socket. */
export const RELAY_CHANNEL_IDLE_SECONDS = 60;
const channelId = z.string().regex(/^ch_[A-Za-z0-9_-]{16,40}$/);
const payload = z.unknown();
export const channelTicketSchema = z.object({ ticket: z.string(), expiresAt: z.string(), url: z.string() });
/** Why a channel or socket ended, in words the app can show. */
export const channelCloseCodeSchema = z.enum([
    "done", // the subscription ended normally (the terminal exited)
    "client_closed",
    "workbench_closed",
    "workbench_offline", // the device's socket went away
    "refused", // the relay or the workbench refused the open (see `reason`)
    "scope", // the device was not granted what the method needs
    "revoked", // the token or the person's membership ended
    "limit", // too many channels, a frame too large, credit overrun
    "idle",
    "protocol", // a frame that does not parse
]);
// ── Client socket ──────────────────────────────────────────────────────────────
/** Client to relay. `ref` is the client's own correlation for an `open`, echoed on `opened` or `closed`. */
export const clientChannelFrameSchema = z.discriminatedUnion("t", [
    z.object({ t: z.literal("open"), ref: z.string().min(1).max(64), rpc: jsonRpcRequestSchema, input: z.boolean().default(false), workspace: z.union([z.string(), z.object({ root: z.string(), target: z.string().optional() })]).optional() }),
    z.object({ t: z.literal("input"), ch: channelId, data: payload }),
    z.object({ t: z.literal("credit"), ch: channelId, bytes: z.number().int().positive().max(16 * 1024 * 1024) }),
    z.object({ t: z.literal("close"), ch: channelId }),
    z.object({ t: z.literal("ping"), at: z.number().optional() }),
]);
/** Relay to client. */
export const relayToClientFrameSchema = z.discriminatedUnion("t", [
    z.object({ t: z.literal("opened"), ref: z.string(), ch: channelId, scopes: z.array(controlScopeSchema), result: payload.optional() }),
    z.object({ t: z.literal("data"), ch: channelId, seq: z.number().int().nonnegative(), data: payload }),
    z.object({ t: z.literal("closed"), ch: channelId.optional(), ref: z.string().optional(), code: channelCloseCodeSchema, reason: z.string().max(300).optional() }),
    z.object({ t: z.literal("pong"), at: z.number().optional() }),
    z.object({ t: z.literal("error"), code: channelCloseCodeSchema, reason: z.string().max(300) }),
]);
// ── Workbench socket ───────────────────────────────────────────────────────────
/** Relay to workbench: the client's open, checked and signed (as `RelayRequest` on the SSE path). */
export const relayToWorkbenchFrameSchema = z.discriminatedUnion("t", [
    z.object({
        t: z.literal("open"),
        ch: channelId,
        clientId: z.string(),
        scopes: z.array(controlScopeSchema),
        rpc: jsonRpcRequestSchema,
        input: z.boolean(),
        workspace: z.union([z.string(), z.object({ root: z.string(), target: z.string().optional() })]).optional(),
        /** The platform's control JWS over `JSON.stringify(rpc)` for this device, person and scopes; verify before serving. */
        signature: z.string(),
    }),
    z.object({ t: z.literal("input"), ch: channelId, data: payload }),
    z.object({ t: z.literal("credit"), ch: channelId, bytes: z.number().int().positive() }),
    z.object({ t: z.literal("close"), ch: channelId, code: channelCloseCodeSchema }),
    z.object({ t: z.literal("ping"), at: z.number().optional() }),
]);
/** Workbench to relay. `opened` may carry the subscription's first answer (a terminal's scrollback). */
export const workbenchChannelFrameSchema = z.discriminatedUnion("t", [
    z.object({ t: z.literal("opened"), ch: channelId, result: payload.optional() }),
    z.object({ t: z.literal("data"), ch: channelId, seq: z.number().int().nonnegative(), data: payload }),
    z.object({ t: z.literal("closed"), ch: channelId, code: channelCloseCodeSchema, reason: z.string().max(300).optional() }),
    z.object({ t: z.literal("pong"), at: z.number().optional() }),
]);
//# sourceMappingURL=relayChannel.js.map